import { decodeForageFrame } from '@continuum/show-core';
import type { TaskDef, TaskState } from '@continuum/show-core';
import type { Era } from './art.js';
import { ERA_SKIN, EraBackdrop, PulseSprite, ShipSprite } from './art.js';
import { decodeRhythmPulse } from './frames.js';
import { mono, sans } from '../ui/atoms.js';

/**
 * What a watcher sees. Each mechanic renders its own live sketch from the frame the
 * player is sending — a glance at their screen, not a video feed.
 */
export function ObserverView({
  def,
  task,
  accent,
  era,
  title,
}: {
  def: TaskDef;
  task: TaskState | undefined;
  accent: string;
  /** The watched player's board is painted in the same era as their own screen. */
  era: Era;
  /** "KESTREL IS PLAYING · LIVE", in the guest's language. */
  title: string;
}) {
  const frame = task?.frame ?? '';
  const n = (key: string, fallback = 0): number => {
    const m = new RegExp(`${key}:(-?[\\d.]+)`).exec(frame);
    return m ? Number(m[1]) : fallback;
  };
  const pulseAt = (i: number) => decodeRhythmPulse(frame, i);
  // THREAD: half the opening, in percent of the field.
  const half = def.type === 'THREAD' ? def.gap * 50 : 16;
  const candle = def.type === 'FORAGE' ? decodeForageFrame(frame) : null;

  return (
    <div>
      <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 1.5, color: accent }}>{title}</div>

      <div
        style={{
          marginTop: 8,
          position: 'relative',
          height: 190,
          borderRadius: 12,
          border: `1px dashed ${accent}55`,
          background: ERA_SKIN[era].bg,
          overflow: 'hidden',
        }}
      >
        <EraBackdrop era={era} opacity={0.3} />
        {def.type === 'THREAD' && (
          <>
            <div
              style={{
                position: 'absolute',
                left: '18%',
                top: `${n('y', 50)}%`,
                width: 26,
                height: 26,
                marginTop: -13,
                transition: 'top .12s linear',
              }}
            >
              <ShipSprite era={era} />
            </div>
            {[0, 1, 2].map((i) => {
              const x = n(`g${i}x`, -20);
              const c = n(`g${i}c`, 50);
              if (x < -10) return null;
              return (
                <div key={i}>
                  <div
                    style={{
                      position: 'absolute',
                      left: `${x}%`,
                      top: 0,
                      width: 16,
                      height: `${c - half}%`,
                      background: `${accent}66`,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: `${x}%`,
                      top: `${c + half}%`,
                      width: 16,
                      bottom: 0,
                      background: `${accent}66`,
                    }}
                  />
                </div>
              );
            })}
          </>
        )}

        {def.type === 'RHYTHM' && (
          <>
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 46,
                height: 2,
                background: accent,
              }}
            />
            {[0, 1, 2].map((i) => {
              const p = pulseAt(i);
              if (!p) return null;
              // Three coils on the player's screen, three here — a watcher should be
              // able to see which one they are about to miss.
              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${p.lane * 33.3 + 16.6}%`,
                    top: `${p.t * 74}%`,
                    transform: 'translateX(-50%)',
                    width: 30,
                    height: 30,
                  }}
                >
                  <PulseSprite era={era} />
                </div>
              );
            })}
          </>
        )}

        {def.type === 'VINE' && (
          <div
            style={{
              position: 'absolute',
              inset: 8,
              display: 'grid',
              gridTemplateColumns: 'repeat(11,1fr)',
              gridTemplateRows: 'repeat(9,1fr)',
              gap: 1,
            }}
          >
            {Array.from({ length: 99 }, (_, i) => {
              const on =
                frame.includes(`,${i},`) || frame.startsWith(`${i},`) || frame.endsWith(`,${i}`);
              const isSeed = n('s', -1) === i;
              return (
                <div
                  key={i}
                  style={{
                    borderRadius: 2,
                    background: isSeed ? '#f6e7a8' : on ? accent : 'rgba(255,255,255,.03)',
                  }}
                />
              );
            })}
          </div>
        )}

        {def.type === 'PLAY' && (
          // The thirty send how far along they are, not their board: the round and the bar.
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', gap: 10, padding: 18 }}>
            <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, letterSpacing: 2, color: accent }}>
              {`${n('r', 0) + 1} / 3`}
            </div>
            <div style={{ width: '80%', height: 10, borderRadius: 5, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
              <div style={{ width: `${task?.progress ?? 0}%`, height: '100%', background: accent, transition: 'width .3s' }} />
            </div>
          </div>
        )}

        {def.type === 'ECHO' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 6,
              padding: 14,
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  borderRadius: 10,
                  border: `1.5px solid ${accent}44`,
                  background: n('l', -1) === i ? accent : `${accent}14`,
                }}
              />
            ))}
          </div>
        )}

        {def.type === 'RECALL' && (
          <div
            style={{
              position: 'absolute',
              inset: 12,
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 5,
            }}
          >
            {Array.from({ length: 10 }, (_, i) => (
              <div
                key={i}
                style={{
                  borderRadius: 5,
                  border: `1px solid ${accent}33`,
                  background: frame.includes(`u${i}`) ? `${accent}44` : 'rgba(255,255,255,.04)',
                }}
              />
            ))}
          </div>
        )}

        {def.type === 'DIVIDE' && (
          <div
            style={{
              position: 'absolute',
              inset: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  position: 'relative',
                  flex: 1,
                  borderRadius: 6,
                  border: `1px solid ${accent}44`,
                  background: `${accent}0d`,
                }}
              >
                {frame.includes(`d${i}`) && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: accent,
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {def.type === 'FORAGE' && candle && (
          <>
            <div
              style={{
                position: 'absolute',
                left: `${candle.x}%`,
                top: `${candle.y}%`,
                width: '40%',
                aspectRatio: '1',
                transform: 'translate(-50%,-50%)',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,196,120,.4) 0%, rgba(224,138,60,0) 70%)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                fontFamily: sans,
                fontSize: 20,
                fontWeight: 700,
                color: accent,
              }}
            >
              {candle.found}
            </div>
          </>
        )}

        {def.type === 'RELAY' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              fontFamily: sans,
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: 6,
              color: accent,
            }}
          >
            {task?.relay?.code ?? '····'}
          </div>
        )}

        <div
          style={{
            position: 'absolute',
            bottom: 8,
            left: 10,
            right: 10,
            height: 4,
            borderRadius: 2,
            background: 'rgba(255,255,255,.1)',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${task?.progress ?? 0}%`,
              background: accent,
              borderRadius: 2,
              transition: 'width .4s',
            }}
          />
        </div>
      </div>
    </div>
  );
}
